import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, input, inject } from '@angular/core';
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { NotificationService } from '@app/core/notifications/notification.service';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { EspaceService } from '@app/features/editeur/services/espace.service';
import { ObjetMaquetteService } from '@app/features/editeur/services/objet-maquette.service';
import { TreeViewService } from '@app/features/editeur/services/tree-view.service';
import { IfIsAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { WithConfirmationDialog } from '@app/shared/dialog/with-confirmation-dialog.decorator';
import { ContexteDetails, TypeEspace } from '@pcscol/odf-api-v1-angular';
import { ConfigService } from '@pcscol-portail/composants-pegase';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first, map, takeUntil, tap } from 'rxjs/operators';
import { TreeNode } from '../tree-node';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip } from '@angular/material/tooltip';
import { NgClass, AsyncPipe } from '@angular/common';
import { ShowIfAutoriseDirective } from '../../../../../shared/autorisations/directives/show-if-autorise.directive';
import { MatIconButton } from '@angular/material/button';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-maquette-noeud',
    templateUrl: './maquette-noeud.component.html',
    styleUrls: ['./maquette-noeud.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { position: 'before' } }],
    imports: [
        NgClass,
        ShowIfAutoriseDirective,
        MatIconButton,
        CdkDragHandle,
        MatTooltip,
        MatIcon,
        MatMenuTrigger,
        MatMenu,
        MatMenuContent,
        MatMenuItem,
        AsyncPipe
    ]
})
export class MaquetteNoeudComponent implements OnDestroy {
    private espaceService = inject(EspaceService);
    private objetMaquetteService = inject(ObjetMaquetteService);
    private _autorisationsService = inject(AutorisationsService);
    private treeView = inject(TreeViewService);
    private commandFactory = inject(CommandFactoryService);
    private configService = inject(ConfigService);
    private clipboard = inject(Clipboard);
    private notificationService = inject(NotificationService);

    private static readonly TOOLTIP_REORDONNANCEMENT_DESACTIVE =
        "Impossible de modifier le lien de l'objet-maquette car un des chemins le contenant est validé";
    private static readonly TOOLTIP_REORDONNANCEMENT_ACTIVE = 'Déplacer cet enfant';

    readonly ODF_AJOUTER_ENFANT = Traitement.ODF_AJOUTER_ENFANT;
    readonly ODF_DECLARER_POINT_INSCRIPTION_ADMINISTRATIVE = Traitement.ODF_DECLARER_POINT_INSCRIPTION_ADMINISTRATIVE;
    readonly ODF_MODIFIER_LIEN_ENFANT = Traitement.ODF_MODIFIER_LIEN_ENFANT;
    readonly ODF_RETIRER_ENFANT = Traitement.ODF_RETIRER_ENFANT;
    readonly ODF_RETIRER_POINT_INSCRIPTION_ADMINISTRATIVE = Traitement.ODF_RETIRER_POINT_INSCRIPTION_ADMINISTRATIVE;
    readonly ODF_SUPPRIMER_OBJET_MAQUETTE = Traitement.ODF_SUPPRIMER_OBJET_MAQUETTE;
    readonly ODF_VALIDER_PARTIE_BASSE = Traitement.ODF_VALIDER_PARTIE_BASSE;
    readonly ODF_VALIDER_PARTIE_HAUTE = Traitement.ODF_VALIDER_PARTIE_HAUTE;
    readonly ODF_LISTER_CONTEXTES = Traitement.ODF_LISTER_CONTEXTES;
    readonly ODF_DESACTIVER_POINT_INSCRIPTION_ADMINISTRATIVE = Traitement.ODF_DESACTIVER_POINT_INSCRIPTION_ADMINISTRATIVE;
    readonly ODF_REACTIVER_POINT_INSCRIPTION_ADMINISTRATIVE = Traitement.ODF_REACTIVER_POINT_INSCRIPTION_ADMINISTRATIVE;
    readonly ODF_ORDONNER_ENFANT = Traitement.ODF_ORDONNER_ENFANT;
    readonly ODF_SUPERGESTIONNAIRE = this.configService.getConfig().odf.traitementSuperGestionnaire;

    @ViewChild(MatMenuTrigger, { static: false }) matMenuTrigger: MatMenuTrigger;
    @Input() objetMaquette: TreeNode;
    readonly modeOrdre = input(false);
    readonly reordonnable = input(false);
    readonly ordonnancementEnCours = input(false);
    @Input() parentReordonne = false;
    @Input() afficherActionsReordonnancement = false;
    @Input() afficherActionModifierOrdreEnfants = false;
    readonly peutPlacerAuDebut = input(false);
    readonly peutMonter = input(false);
    readonly peutDescendre = input(false);
    readonly peutPlacerALaFin = input(false);
    @Output() detached = new EventEmitter<TreeNode>();
    @Output() supprime = new EventEmitter<TreeNode>();
    @Output() pointInscriptionAdministrativeToggle = new EventEmitter<TreeNode>();
    @Output() rendreObligatoire = new EventEmitter<TreeNode>();
    @Output() rendreFacultatif = new EventEmitter<TreeNode>();
    @Output() openAllNodesOfSameDepth = new EventEmitter<void>();
    @Output() modifierOrdreEnfants = new EventEmitter<TreeNode>();
    @Output() placerEnfantAuDebut = new EventEmitter<TreeNode>();
    @Output() monterEnfant = new EventEmitter<TreeNode>();
    @Output() descendreEnfant = new EventEmitter<TreeNode>();
    @Output() placerEnfantALaFin = new EventEmitter<TreeNode>();

    private _hasSomeContextValidated = new BehaviorSubject<boolean | 'idle' | 'loading'>('idle');
    private destroyed$ = new Subject<void>();

    isSandBox: boolean;

    constructor() {
        this.isSandBox = this.espaceService.espaceSelected?.typeEspace === TypeEspace.BacASable;
    }
    readonly isSuperGestionnaire$ = this._autorisationsService.isSuperGestionnaire$;

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    onRightClick() {
        this.matMenuTrigger.openMenu();
    }

    onRendreObligatoire() {
        this.rendreObligatoire.emit(this.objetMaquette);
    }

    onRendreFacultatif() {
        this.rendreFacultatif.emit(this.objetMaquette);
    }

    @WithConfirmationDialog({
        titre: "Supprimer l'objet",
        messageTexte: [
            args =>
                `Vous allez supprimer l'objet ${args[0].code} et toute sa descendance. Cet objet ne sera plus présent dans cet espace de travail`,
            `Souhaitez vous poursuivre ?`
        ],
        confirmButtonLabel: 'Supprimer',
        confirmButtonIcon: 'delete'
    })
    onSupprimer(objetMaquette) {
        this.supprime.emit(objetMaquette);
    }

    onDetacher() {
        this.detached.emit(this.objetMaquette);
    }

    onAjouterPIA() {
        this.pointInscriptionAdministrativeToggle.emit(this.objetMaquette);
    }

    selectParentPourAjoutEnfant(enfantDejaExistant: boolean) {
        this.treeView.selectObjetMaquette(this.objetMaquette);
        this.treeView.selectParentForAddingChild({ parent: this.objetMaquette, enfantDejaExistant });
        this.treeView.openSidebar();
    }

    onSelected() {
        this.treeView.selectObjetMaquette(this.objetMaquette);
    }

    onExpandAllNodesOfSameDepth() {
        this.openAllNodesOfSameDepth.emit();
    }

    onModifierOrdreDesEnfants() {
        this.modifierOrdreEnfants.emit(this.objetMaquette);
    }

    onPlacerEnfantAuDebut() {
        this.placerEnfantAuDebut.emit(this.objetMaquette);
    }

    onMonterEnfant() {
        this.monterEnfant.emit(this.objetMaquette);
    }

    onDescendreEnfant() {
        this.descendreEnfant.emit(this.objetMaquette);
    }

    onPlacerEnfantALaFin() {
        this.placerEnfantALaFin.emit(this.objetMaquette);
    }

    isControleDeReordonnancementDesactive(
        contexteValide: boolean | 'loading' | 'idle' | null,
        estSuperGestionnaire: boolean | null
    ): boolean {
        return this.ordonnancementEnCours() || this.isModificationLienEnfantDesactive(contexteValide, estSuperGestionnaire);
    }

    isActionModifierOrdreEnfantsDesactive(
        contexteValide: boolean | 'loading' | 'idle' | null,
        estSuperGestionnaire: boolean | null
    ): boolean {
        return (
            this.ordonnancementEnCours() ||
            (!this.parentReordonne && this.isModificationLienEnfantDesactive(contexteValide, estSuperGestionnaire))
        );
    }

    getToolTipModifierOrdreEnfants(contexteValide: boolean | 'loading' | 'idle' | null, estSuperGestionnaire: boolean | null): string {
        const tooltipActif = this.parentReordonne ? 'Quitter le réordonnancement' : 'Réordonner les enfants';
        return this.parentReordonne
            ? tooltipActif
            : this.getToolTipModificationLienEnfant(contexteValide, estSuperGestionnaire, tooltipActif);
    }

    isModificationLienEnfantDesactive(contexteValide: boolean | 'loading' | 'idle' | null, estSuperGestionnaire: boolean | null): boolean {
        return contexteValide !== false && estSuperGestionnaire === false;
    }

    isModificationLienEnfantInterditeParValidation(
        contexteValide: boolean | 'loading' | 'idle' | null,
        estSuperGestionnaire: boolean | null
    ): boolean {
        return contexteValide === true && estSuperGestionnaire === false;
    }

    getToolTipControleDeReordonnancement(
        contexteValide: boolean | 'loading' | 'idle' | null,
        estSuperGestionnaire: boolean | null
    ): string {
        return this.isModificationLienEnfantInterditeParValidation(contexteValide, estSuperGestionnaire)
            ? MaquetteNoeudComponent.TOOLTIP_REORDONNANCEMENT_DESACTIVE
            : MaquetteNoeudComponent.TOOLTIP_REORDONNANCEMENT_ACTIVE;
    }

    getToolTipModificationLienEnfant(
        contexteValide: boolean | 'loading' | 'idle' | null,
        estSuperGestionnaire: boolean | null,
        tooltipActif: string
    ): string {
        return this.isModificationLienEnfantInterditeParValidation(contexteValide, estSuperGestionnaire)
            ? MaquetteNoeudComponent.TOOLTIP_REORDONNANCEMENT_DESACTIVE
            : tooltipActif;
    }

    get hasPIAActifInChildren(): boolean {
        return this.recursiveSearchForPIAActifInChildren(this.objetMaquette);
    }

    recursiveSearchForPIAActifInChildren(om: TreeNode): boolean {
        return om.enfants?.some(c => c.objetMaquette.pia?.actif || this.recursiveSearchForPIAActifInChildren(c.objetMaquette));
    }

    get allDirectChildsAreValidated(): boolean {
        return this.objetMaquette.enfants?.every(c => c.objetMaquette.valide);
    }

    get atLeastOneChildIsValidated(): boolean {
        return this.objetMaquette.enfants?.some(c => c.objetMaquette.valide);
    }

    get canAddPIA(): boolean {
        return (
            !!this.objetMaquette.contexteId &&
            !this.objetMaquette.pia &&
            !this.objetMaquette.piaPresentSurUnAncetre?.piaActif &&
            !this.objetMaquette.valide &&
            !this.hasPIAActifInChildren
        );
    }

    get canAddPIAAsSupergestionnaire(): boolean {
        return (
            !!this.objetMaquette.contexteId &&
            !this.objetMaquette.pia &&
            !this.objetMaquette.piaPresentSurUnAncetre?.piaActif &&
            this.objetMaquette.valide &&
            !this.hasPIAActifInChildren
        );
    }

    get canRemovePIA(): boolean {
        return (
            !!this.objetMaquette.contexteId &&
            this.objetMaquette.pia &&
            !this.objetMaquette.piaPresentSurUnAncetre?.piaActif &&
            !this.objetMaquette.valide &&
            !this.hasPIAActifInChildren &&
            !this.objetMaquette.valide
        );
    }

    get canTogglePartieAdmin(): boolean {
        return !!this.objetMaquette.pia && !this.objetMaquette.valide;
    }

    get canValidatePartiePedagogique(): boolean {
        return !this.hasPIAActifInChildren && this.objetMaquette.piaPresentSurUnAncetre && !this.objetMaquette.valide;
    }

    get canDevalidatePartiePedagogique(): boolean {
        return !!this.objetMaquette.piaPresentSurUnAncetre && !this.hasPIAActifInChildren && this.objetMaquette.valide;
    }

    hasSomeContextValidated(): Observable<boolean | 'loading' | 'idle'> {
        this.getContextsIfNecessary();
        return this._hasSomeContextValidated.asObservable();
    }

    @IfIsAuthorized(Traitement.ODF_LISTER_CONTEXTES)
    private getContextsIfNecessary() {
        if (this._hasSomeContextValidated.getValue() === 'idle') {
            this._hasSomeContextValidated.next('loading');
            this.objetMaquetteService
                .listerContextes(this.objetMaquette.id)
                .pipe(
                    map((ctx: ContexteDetails[]) => ctx.some(c => c.valide)),
                    tap((value: boolean) => this._hasSomeContextValidated.next(value)),
                    takeUntil(this.destroyed$)
                )
                .subscribe();
        }
    }

    @WithConfirmationDialog({
        titre: 'AVERTISSEMENT: la validation est irréversible.  Aucun retour en mode "non validé" ne sera possible.',
        messageHtml: [
            `<p>Les objets compris entre l'objet porteur d'inscription administrative et la formation seront validés: </p>`,
            `<ul>`,
            `<li>Il ne sera plus possible de détacher un de ces objets ou d'intercaler un nouvel objet entre 2 de ces objets validés.</li>`,
            `<li>Il ne sera plus possible de modifier les descripteurs de ces objets - y compris contextuels.`,
            `Seul un gestionnaire ayant accès à des droits supplémentaires pourra modifier les descripteurs d'un objet maquette validé.</li>`,
            `</ul><br />`,
            `<p>Souhaitez vous confirmer la validation ?</p>`
        ]
    })
    validerAdministrativement() {
        this.commandFactory
            .validerPartieHauteCommand(this.objetMaquette.id, this.objetMaquette.contexteId!)
            .do()
            .pipe(first())
            .subscribe(() => this.treeView.refresh());
    }

    @WithConfirmationDialog({
        titre: "Inactiver le caractère PIA de l'objet",
        messageTexte: [
            `Vous allez inactiver le caractère porteur d'inscription administrative d'un objet.`,
            `Cette information sera communiquée aux autres services et aura pour effet d'interdire de nouvelles inscriptions sur cet objet.`
        ]
    })
    inactiverPia(): void {
        this.commandFactory
            .inactiverPointInscriptionAdministrativeCommand(this.objetMaquette.id, '0', this.objetMaquette.contexteId!)
            .do()
            .pipe(first())
            .subscribe(() => this.treeView.refresh());
    }

    reactiverPia(): void {
        this.commandFactory
            .reactiverPointInscriptionAdministrativeCommand(this.objetMaquette.id, '0', this.objetMaquette.contexteId!)
            .do()
            .pipe(first())
            .subscribe(() => this.treeView.refresh());
    }

    @WithConfirmationDialog({
        titre: 'AVERTISSEMENT:',
        messageHtml: [
            `<h1><p>La validation est irréversible: Aucun retour en mode "non validé" ne sera possible.</h1></p>`,
            `<p>Tous les objets situés sur la branche de l'objet sélectionné, en remontant jusqu'au PIA et en redescendant jusqu'à l'élément le plus bas , seront validés: </p>`,
            `<ul>`,
            `<li>Il ne sera plus possible de détacher un de ces objets ou d'intercaler un nouvel objet entre 2 de ces objets validés.</li>`,
            `<li>Il ne sera plus possible de modifier les descripteurs de ces objets - y compris contextuels.`,
            `Seul un gestionnaire ayant accès à des droits supplémentaires pourra modifier les descripteurs d'un objet maquette validé.</li>`,
            `</ul><br />`,
            `<h1><p>Pour les objets mutualisés: la validation doit être faite contexte par contexte</h1></p>`,
            `<ul>`,
            `<li>Si vous validez un objet mutualisé qui a une descendance, il faut aller vérifier si tous ses enfants ont le même statut pour chaque contexte où cet objet  est validé.</li>`,
            `<li>Si vous validez un objet mutualisé qui a un père mutualisé, il faut aller vérifier si tous les enfants de cet objet père  ont le même statut pour chaque contexte où cet objet père est validé.</li>`,
            `</ul><br />`,
            `<p>Souhaitez vous confirmer la validation ?</p>`
        ]
    })
    validerPedagogiquement() {
        this.commandFactory
            .validerPartieBasseCommand(this.objetMaquette.id, this.objetMaquette.contexteId!)
            .do()
            .pipe(first())
            .subscribe(() => this.treeView.refresh());
    }

    copyLinkToClipboard() {
        const urlParams: string[] = location.href.split('/');
        urlParams.pop();

        const routeACopier: string = `${urlParams.join('/')}/${this.objetMaquette.id}`;
        const success: boolean = this.clipboard.copy(routeACopier);
        if (success) {
            this.notificationService.success(`Le lien vers l'objet maquette a bien été copié dans le presse-papier.`);
        }
    }
}
